/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import { useIsTruncated } from '@hyphengroup/cms-components/utils/useIsTruncated';
import { LeadCapturePage_attributeList } from '__generated__/graphql';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { JSX, useEffect, useState, useRef } from 'react';
import { first } from 'utils/array';
import { Translated } from 'utils/staticText';
import { getVerticalNameFromProductUid } from '../../lib/product';
import {
  ITrackingProductPageBaseParameters,
  trackProductDetailsClose,
  trackProductDetailsRewardDetailsExpandOrCollapse,
  trackProductDetailsRewardDetailsTaCExpandOrCollapse,
} from '../../lib/tracking';
import QuickFacts from '../QuickFacts';
import { CatalogProductFragment } from '../types';
import { ProductDialogStaticText } from './i18n';
import {
  CatalogueProductOffers,
  getAllActiveOffers,
  getRewardDetails,
  getSortedGifts,
} from '$/(application)/components/rewardOffersUtils';
import SupportedPayment from '$/(application)/components/SupportedPayment';
import ArrowButton from '$/components/icons/ArrowBottom';
import ArrowUp from '$/components/icons/ArrowUp';
import CheckStart from '$/components/icons/CheckStar';
import Close from '$/components/icons/Close';
import InfoCircle from '$/components/icons/InfoCircle';
import Like from '$/components/icons/Like';

interface additionalAttributeType {
  prefix?: string;
  suffix?: string;
  value?: number | string;
  label?: string;
  values?: string[];
  attributeType?: string;
  additionalInformation?: string;
  keyValue?: {
    key?: string;
    values?: string[];
  }[];
}

export function ProductDialog(props: {
  setOpen: (value: boolean) => void;
  isOpen: boolean;
  scrollToSection?: string | null;
  product: CatalogProductFragment;
  attributes: LeadCapturePage_attributeList['attributeList'];
  staticText: Translated<typeof ProductDialogStaticText>;
  tracking: ITrackingProductPageBaseParameters;
}): JSX.Element {
  const { setOpen, tracking } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [descriptionRef, shouldShowButton] = useIsTruncated();
  const tagLabelMobileRef = useRef<HTMLSpanElement>(null);
  const [tagLabelMobileHeight, setTagLabelMobileHeight] = useState(0);
  const tagLabelDesktopRef = useRef<HTMLSpanElement>(null);
  const [tagLabelDesktopHeight, setTagLabelDesktopHeight] = useState(0);
  const heightTagLabel = 30;
  const ProductdialogRef = useRef<HTMLDivElement | null>(null);
  const verticalCode = getVerticalNameFromProductUid(props.product?.productUid);

  const rewardsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trackProductDetailsClose({
          ...tracking,
          source: 'Close',
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [setOpen, tracking]);

  useEffect(() => {
    if (props.isOpen && props.scrollToSection) {
      setTimeout(() => {
        if (props.scrollToSection === 'rewards' && rewardsRef.current) {
          rewardsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  }, [props.isOpen, props.scrollToSection]);

  useEffect(() => {
    const checkTagLabelMobileHeight = () => {
      const tagLabelMobile = tagLabelMobileRef.current;
      if (tagLabelMobile) {
        const currentHeightMobile = tagLabelMobile.clientHeight;
        setTagLabelMobileHeight(currentHeightMobile);
      }
    };

    checkTagLabelMobileHeight();
    if (props.isOpen) {
      checkTagLabelMobileHeight();
    }
    const resizeListener = () => checkTagLabelMobileHeight();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', checkTagLabelMobileHeight);
    };
  }, [props.isOpen]);

  useEffect(() => {
    const checkTagLabelDesktopHeight = () => {
      const tagLabelDesktop = tagLabelDesktopRef.current;
      if (tagLabelDesktop) {
        const currentHeightDesktop = tagLabelDesktop.clientHeight;
        setTagLabelDesktopHeight(currentHeightDesktop);
      }
    };

    checkTagLabelDesktopHeight();
    if (props.isOpen) {
      checkTagLabelDesktopHeight();
    }
    const resizeListener = () => checkTagLabelDesktopHeight();
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', checkTagLabelDesktopHeight);
    };
  }, [props.isOpen]);

  useEffect(() => {
    if (props.isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [props.isOpen]);

  useEffect(() => {
    const handleClickOutsideDialog = (event: MouseEvent) => {
      if (
        ProductdialogRef.current &&
        !ProductdialogRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        trackProductDetailsClose({
          ...tracking,
          source: 'Modal Outside',
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutsideDialog);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDialog);
    };
  }, [setOpen, tracking]);

  const productCardTopBadge = props.product?.productCardTopBadge;
  const tag =
    productCardTopBadge?.__typename === 'KeyValueTextAttribute'
      ? first(first(productCardTopBadge.value)?.values)
      : undefined;

  const quickFactsAttr = props.attributes?.find(
    (item) => item?.attributeType === 'QUICK_FACTS',
  ) as additionalAttributeType;

  const eligibility = {
    minimumAnnualIncome:
      props.attributes?.find(
        (item) => item?.attributeType === 'MINIMUM_ANNUAL_INCOME',
      ) ?? undefined,
    minimumAge:
      props.attributes?.find((item) => item?.attributeType === 'MINIMUM_AGE') ??
      undefined,
    citizenship:
      props.attributes?.find((item) => item?.attributeType === 'CITIZENSHIP') ??
      undefined,
  };

  const filteredEligibility = Object.fromEntries(
    Object.entries(eligibility).filter(([_, value]) => value !== undefined),
  );

  /*Get Rewards*/
  const exclusiveOffers = (props.product as CatalogueProductOffers)
    ?.exclusiveOffers;

  const allActiveOffers = getAllActiveOffers(
    props.product as CatalogueProductOffers,
  );

  const [openOffers, setOpenOffers] = useState<boolean[]>(
    Array(allActiveOffers.length).fill(true),
  );
  const toggleOffer = (index: number) => {
    setOpenOffers((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const [openTermCondition, setIsOpenTermCondition] = useState<boolean[]>(
    Array(allActiveOffers.length).fill(false),
  );
  const toggleOfferTC = (index: number) => {
    setIsOpenTermCondition((prev) => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  const processingFeeAttr = props.attributes?.find(
    (item) => item?.attributeType === 'PROCESSING_FEE',
  ) as additionalAttributeType;

  const supportedPayments = [
    'SUPPORTED_PAYMENT_NETWORK',
    'SUPPORTED_PAYMENT_TYPE',
  ];

  const renderEligibilityValue = (value: additionalAttributeType) => {
    const { attributeType, prefix, suffix, value: val } = value;

    switch (attributeType) {
      case 'MINIMUM_ANNUAL_INCOME':
        return `${prefix ?? ''}${Number(val).toLocaleString()}`;
      case 'MINIMUM_AGE':
        return `${val ?? ''} ${suffix ?? ''}`;
      default:
        return `${prefix ?? ''} ${val ?? ''} ${suffix ?? ''}`;
    }
  };

  return (
    <>
      {props.isOpen && (
        <div className="fixed inset-0 z-10">
          <div className="size-full items-center justify-center bg-[rgba(0,0,0,0.5)] lg:flex">
            <div className="p-4 md:p-6">
              <div
                className="lg:mhg-alpha-spacing-padding-y-spacing-padding-section-x mhg-alpha-no-scrollbar inline-flex max-h-[95vh] w-full shrink-0 flex-col items-center gap-[--spacing-gap-section-y-lg] overflow-y-scroll rounded-[--line-height-badge] bg-[--fill-alternative-1] lg:w-[--general-narrow-content-max-width-3] lg:max-w-[--general-narrow-content-max-width-3] lg:gap-[--spacing-gap-x] lg:rounded-[--radius-card]"
                ref={ProductdialogRef}
              >
                <div className="absolute hidden w-full max-w-[--general-narrow-content-max-width-3] items-end justify-end px-[--spacing-padding-section-x] lg:flex">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="right-[--spacing-padding-x] top-[--spacing-padding-y] z-1 flex size-[--button-size-regular] cursor-pointer items-center justify-center gap-[--button-gap] rounded-[--radius-button] shadow-md bg-[--buttons-base] text-[--buttons-neutral-dark] hover:bg-[--buttons-neutral-dark] hover:text-[--buttons-base]"
                      onClick={() => {
                        props.setOpen(false);
                        trackProductDetailsClose({
                          ...props.tracking,
                          source: 'Close',
                        });
                      }}
                    >
                      <Close className="size-[--shapes-icon-size-lg]" />
                    </button>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-center gap-[--spacing-gap-section-y-lg] self-stretch rounded-[--line-height-badge] bg-[--fill-alternative-1] lg:flex-row lg:items-start lg:gap-[--spacing-gap-x] lg:rounded-[--radius-card]">
                  <div className="absolute right-4 top-4 md:right-6 md:top-6 lg:hidden">
                    <button
                      type="button"
                      className="absolute right-[--spacing-padding-x] top-[--spacing-padding-y] flex size-[--button-size-regular] cursor-pointer items-center justify-center gap-[--button-gap] rounded-[--radius-button] shadow-md bg-[--buttons-base] text-[--buttons-neutral-dark] hover:bg-[--buttons-neutral-dark] hover:text-[--buttons-base]"
                      onClick={() => {
                        props.setOpen(false);
                        trackProductDetailsClose({
                          ...props.tracking,
                          source: 'Close',
                        });
                      }}
                    >
                      <Close className="size-[--shapes-icon-size-lg]" />
                    </button>
                  </div>
                  <div className="mhg-alpha-spacing-padding-y-spacing-padding-section-x lg:mhg-alpha-box-shadow-1 lg:mhg-alpha-spacing-padding-y-spacing-padding-x flex w-full flex-col items-center gap-[--spacing-gap-section-y] rounded-t-[--line-height-badge] bg-[--fill-card] lg:sticky lg:top-0 lg:w-full lg:min-w-95 lg:max-w-95 lg:rounded-[--radius-card]">
                    <div className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch lg:hidden">
                      {tag && (
                        <span
                          ref={tagLabelMobileRef}
                          className={`mhg-alpha-tag flex items-center justify-center gap-[--button-gap] bg-[--highlights-gray-light] text-[--highlights-gray-dark] ${tagLabelMobileHeight < heightTagLabel ? 'rounded-[--tag-size] text-center' : 'rounded-[--radius-button] px-[--button-padding-regular-x-icon] py-[--spacing-padding-y-xs] text-left'}`}
                        >
                          {tag}
                        </span>
                      )}
                      <div className="mhg-alpha-display-1 flex text-[--text-primary] lg:hidden">
                        {props.product?.productName}
                      </div>
                    </div>
                    {tag && (
                      <div className="hidden flex-col items-start gap-[--spacing-gap-y-sm] self-stretch lg:flex">
                        <span
                          ref={tagLabelDesktopRef}
                          className={`mhg-alpha-tag flex items-center justify-center gap-[--button-gap] bg-[--highlights-gray-light] text-[--highlights-gray-dark] ${tagLabelDesktopHeight < heightTagLabel ? 'rounded-[--tag-size] text-center' : 'rounded-[--radius-button] px-[--button-padding-regular-x-icon] py-[--spacing-padding-y-xs] text-left'}`}
                        >
                          {tag}
                        </span>
                      </div>
                    )}
                    <div className="w-full lg:hidden">
                      <img
                        className="flex w-35 flex-col items-start"
                        src={props.product?.productImage}
                        alt={props.product?.productName}
                      />
                    </div>
                    <div
                      className={classNames(
                        'mhg-alpha-spacing-padding-y-spacing-padding-x hidden w-full flex-col items-center justify-center bg-[--fill-card] lg:flex',
                        {
                          'rounded-[--radius-card-inner] border border-solid border-[--borders-default]':
                            verticalCode !== 'CC',
                        },
                      )}
                    >
                      <img
                        className="object-contain"
                        src={props.product?.productImage}
                        alt={props.product?.productName}
                      />
                    </div>
                    {props.product?.description && (
                      <div className="flex flex-col items-end justify-start gap-2 self-stretch lg:hidden">
                        <div
                          ref={descriptionRef}
                          className={`mhg-alpha-body-1 text-[--text-primary] ${isExpanded ? 'line-clamp-none' : 'line-clamp-3'}`}
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: props.product.description.replace(
                              /\n/g,
                              '<br />',
                            ),
                          }}
                        ></div>
                        {shouldShowButton && (
                          <button
                            type="button"
                            className="mhg-alpha-body-1 flex flex-row items-center justify-center gap-[--spacing-gap-x-xs] text-[--text-secondary-lighter]"
                            onClick={() => {
                              setIsExpanded(!isExpanded);
                            }}
                          >
                            <span>
                              {isExpanded
                                ? props.staticText.dialogSeeLess
                                : props.staticText.dialogSeeMore}
                            </span>
                            {isExpanded ? (
                              <ArrowUp className="size-[--shapes-icon-size-lg]" />
                            ) : (
                              <ArrowButton className="size-[--shapes-icon-size-lg]" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex grow flex-col items-start self-stretch px-[--spacing-padding-x] pb-[--spacing-padding-x] pt-0 lg:!p-0">
                    <div className="hidden flex-col items-start gap-[--spacing-gap-y] self-stretch lg:flex">
                      <div className="mhg-alpha-display-2 w-11/12 text-[--text-primary]">
                        {props.product?.productName}
                      </div>
                      {props.product?.description && (
                        <div
                          className="mhg-alpha-body-1-relaxed text-[--text-primary]"
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: props.product.description.replace(
                              /\n/g,
                              '<br />',
                            ),
                          }}
                        ></div>
                      )}
                    </div>

                    <QuickFacts
                      label={quickFactsAttr?.label ?? ''}
                      keyValue={
                        quickFactsAttr?.keyValue?.map((item) => ({
                          key: item.key ?? '',
                          values: item.values ?? [],
                        })) ?? []
                      }
                      staticText={props.staticText}
                      tracking={props.tracking}
                    />

                    {Object.values(filteredEligibility).some(
                      (value) =>
                        value?.label ||
                        (value as additionalAttributeType)?.value,
                    ) && (
                      <div className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch pt-[--spacing-gap-y-sm]">
                        <div className="flex items-center justify-center gap-[10px] px-[--spacing-padding-y] py-0">
                          <CheckStart className="size-6" />
                          <span className="mhg-alpha-subheading-2 text-center text-[--text-accent]">
                            Eligibility
                          </span>
                        </div>
                        <div className="mhg-alpha-box-shadow-1 flex flex-col items-start self-stretch rounded-b-[--radius-card] rounded-t-none border-r border-solid border-r-[--borders-default] bg-[--fill-card]">
                          <div className="flex items-start self-stretch border-t-2 border-solid border-t-[--fill-primary] pb-[--spacing-padding-y-xs] lg:pb-0">
                            <div className="flex flex-[1_0_0] flex-col items-start gap-[--spacing-gap-y-sm] px-[--spacing-padding-x-sm] pb-0 pt-[--spacing-padding-y] lg:px-[--spacing-padding-x]">
                              {Object.entries(filteredEligibility).map(
                                ([key, value], index) => (
                                  <div
                                    className={`flex flex-col items-start gap-[--spacing-gap-y-xs] self-stretch pb-[--button-padding-regular-x-icon] ${index === Object.entries(filteredEligibility).length - 1 ? '' : 'border-b border-solid border-b-[--colors-neutral-gray-300]'}`}
                                    key={key}
                                  >
                                    <div className="flex flex-col items-start gap-[--spacing-gap-x-xs] self-stretch lg:flex-row lg:items-center lg:justify-between">
                                      <div className="mhg-alpha-body-2-relaxed text-[--text-secondary-lighter]">
                                        {value?.label}
                                      </div>
                                      <div className="mhg-alpha-bullet-2 flex items-center gap-1 text-[--text-primary]">
                                        {value &&
                                          renderEligibilityValue(
                                            value as additionalAttributeType,
                                          )}
                                        {(value as additionalAttributeType)
                                          ?.additionalInformation && (
                                          <div className="group relative">
                                            <InfoCircle className="size-shapes/icon-size-md text-[color:--colors-neutral-gray-700] text-[length:--shapes-icon-size-md]" />
                                            <div className="mhg-alpha-caption absolute bottom-full left-1/2 mb-0 w-max max-w-50 translate-x-2 rounded-lg p-3 opacity-0 shadow transition-opacity duration-200 bg-white text-[--text-primary] text-sm group-hover:opacity-100 md:max-w-75 lg:left-auto lg:right-0 lg:mb-2">
                                              {
                                                (
                                                  value as additionalAttributeType
                                                )?.additionalInformation
                                              }
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {allActiveOffers.length > 0 && (
                      <div
                        ref={rewardsRef}
                        className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch pt-[--spacing-gap-y-sm]"
                      >
                        <div className="flex items-center justify-center gap-[10px] px-[--spacing-padding-y] py-0">
                          <Like className="size-6" />
                          <span className="mhg-alpha-subheading-2 text-center text-[--text-accent]">
                            {props.staticText.dialogRewardsTitle}
                          </span>
                        </div>
                        <div className="mhg-alpha-box-shadow-1 flex flex-col items-start self-stretch rounded-b-[--radius-card] rounded-t-none border-r border-solid border-r-[--borders-default] bg-[--fill-card] lg:bg-[--fill-primary-light]">
                          <div className="flex items-start self-stretch border-t-2 border-solid border-t-[--fill-primary] pb-[--spacing-padding-y-xs]">
                            <div className="flex flex-1 flex-col items-start gap-4 p-[--spacing-padding-y]">
                              {allActiveOffers.map((offer, index) => (
                                <div
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={index}
                                  className="flex w-full flex-col items-start gap-4 md:rounded-[--radius-card-inner] md:px-[--spacing-padding-x-sm] md:py-[--spacing-padding-y-sm] md:bg-white"
                                >
                                  <div className="flex w-full items-center justify-between self-stretch">
                                    <button
                                      onClick={() => {
                                        toggleOffer(index);
                                        trackProductDetailsRewardDetailsExpandOrCollapse(
                                          {
                                            ...props.tracking,
                                            sectionTitle: offer.offerTitle,
                                            isCollapsed: openOffers[index],
                                          },
                                        );
                                      }}
                                      type="button"
                                      className="flex w-full items-center gap-[--button-gap] rounded-[--tag-size]"
                                    >
                                      <span
                                        className={`
    mhg-alpha-tag-font inline-block rounded-[--tag-size] px-[--tag-padding-x] text-left
    ${(exclusiveOffers || []).includes(offer) ? 'bg-[--highlights-blue-light] text-[--highlights-blue-dark]' : 'bg-[--highlights-gray-light] text-[--highlights-gray-dark]'}
    line-clamp-2
    ${offer.offerTitle.length > 35 ? 'pl-[--spacing-3]' : ''}
    `}
                                      >
                                        {offer.offerTitle}
                                      </span>
                                      <span className="ml-auto shrink-0">
                                        {' '}
                                        {openOffers[index] ? (
                                          <ArrowUp className="size-[--shapes-icon-size-lg] lg:hidden" />
                                        ) : (
                                          <ArrowButton className="size-[--shapes-icon-size-lg] lg:hidden" />
                                        )}
                                      </span>
                                    </button>
                                  </div>

                                  {openOffers[index] && (
                                    <>
                                      <span className="mhg-alpha-body-2 self-stretch font-[--font-weight-body-1] text-[--text-secondary]">
                                        {
                                          props.staticText
                                            .dialogRewardsDescription
                                        }
                                      </span>

                                      <div className="flex flex-col items-start gap-[--spacing-gap-y] self-stretch">
                                        <div className="flex flex-col items-start gap-[--spacing-gap-y-xs] self-stretch">
                                          <span className="mhg-alpha-body-2 h-7 self-stretch font-[--font-weight-subheading-2] text-[--text-secondary] leading-[--line-height-body-2-relaxed]">
                                            {
                                              props.staticText
                                                .dialogRewardsOption
                                            }
                                          </span>
                                          <span className="mhg-alpha-body-2 mhg-alpha-body-list self-stretch font-[--font-weight-body-1] text-[--text-secondary] [&_ul_li_ul_li]:font-[var(--font-weight-display-1)]">
                                            <ul>
                                              {offer.gifts?.length ? (
                                                getSortedGifts(offer.gifts).map(
                                                  (gift, idx) => (
                                                    <li
                                                      key={gift.giftUid || idx}
                                                    >
                                                      {gift.criteriaList
                                                        ?.length ? (
                                                        <>
                                                          {getRewardDetails(
                                                            gift.criteriaList,
                                                          )}
                                                          <ul>
                                                            <li>
                                                              {gift.giftTitle}
                                                            </li>
                                                          </ul>
                                                        </>
                                                      ) : (
                                                        <span className="font-[--font-weight-display-1]">
                                                          {gift.giftTitle}
                                                        </span>
                                                      )}
                                                    </li>
                                                  ),
                                                )
                                              ) : (
                                                <span className="font-[--font-weight-display-1]">
                                                  {offer.gifts?.[0]?.giftTitle}
                                                </span>
                                              )}
                                            </ul>
                                          </span>
                                        </div>
                                      </div>

                                      <div className="flex w-full flex-col items-start gap-[--spacing-gap-y-xs] self-stretch rounded-[--radius-card-inner] px-[--spacing-padding-x-sm] py-[--spacing-padding-y-sm] bg-[--fill-alternative-1]">
                                        <div className="flex w-full items-center justify-between self-stretch pr-2">
                                          <button
                                            onClick={() => {
                                              toggleOfferTC(index);
                                              trackProductDetailsRewardDetailsTaCExpandOrCollapse(
                                                {
                                                  ...props.tracking,
                                                  sectionTitle:
                                                    offer.offerTitle,
                                                  isCollapsed:
                                                    openTermCondition[index],
                                                },
                                              );
                                            }}
                                            type="button"
                                            className="flex w-full items-center gap-[--button-gap] rounded-[--tag-size]"
                                          >
                                            <span className="mhg-alpha-body-2 font-[--font-weight-subheading-2] text-[--text-secondary-lighter] leading-[--line-height-button-1]">
                                              {props.staticText.dialogRewardsTC}
                                            </span>
                                            <span className="ml-auto shrink-0">
                                              {openTermCondition[index] ? (
                                                <ArrowUp className="size-[--shapes-icon-size-lg]" />
                                              ) : (
                                                <ArrowButton className="size-[--shapes-icon-size-lg]" />
                                              )}
                                            </span>
                                          </button>
                                        </div>

                                        {openTermCondition[index] && (
                                          <div
                                            className="mhg-alpha-body-list mhg-alpha-body-2  mhg-alpha-body-list-table font-[--font-weight-body-1] text-[--text-secondary] [&_tbody_tr_td]:p-0 md:[&_tbody_tr_td]:p-spacing/padding/x-xs"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                              __html: DOMPurify.sanitize(
                                                offer.description,
                                              ),
                                            }}
                                          ></div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {processingFeeAttr && processingFeeAttr.value && (
                      <div className="flex flex-col items-start gap-[--spacing-gap-y-sm] self-stretch pt-[--spacing-gap-y-sm]">
                        <div className="flex items-center justify-center gap-[10px] px-[--spacing-padding-y] py-0">
                          <CheckStart className="size-6" />
                          <span className="mhg-alpha-subheading-2 text-center text-[--text-accent]">
                            Fees
                          </span>
                        </div>
                        <div className="mhg-alpha-box-shadow-1 flex flex-col items-start self-stretch rounded-b-[--radius-card] rounded-t-none border-r border-solid border-r-[--borders-default] bg-[--fill-card]">
                          <div className="flex items-start self-stretch border-t-2 border-solid border-t-[--fill-primary] pb-[--spacing-padding-y-xs] lg:pb-0">
                            <div className="flex flex-[1_0_0] flex-col items-start gap-[--spacing-gap-y-sm] px-[--spacing-padding-x-sm] pb-0 pt-[--spacing-padding-y] lg:px-[--spacing-padding-x]">
                              <div className="flex flex-col items-start gap-[--spacing-gap-y-xs] self-stretch pb-[--button-padding-regular-x-icon]">
                                <div className="flex flex-col items-start gap-[--spacing-gap-x-xs] self-stretch lg:flex-row lg:items-center lg:justify-between">
                                  <div className="mhg-alpha-body-2-relaxed text-[--text-secondary-lighter]">
                                    {processingFeeAttr.label}
                                  </div>
                                  <div className="mhg-alpha-bullet-2 flex items-center gap-1 text-[--text-primary]">
                                    {processingFeeAttr.prefix ?? ''}{' '}
                                    {processingFeeAttr.value ?? ''}
                                    {processingFeeAttr.suffix ?? ''}
                                    {processingFeeAttr.additionalInformation && (
                                      <div className="group relative">
                                        <InfoCircle className="size-shapes/icon-size-md text-[color:--colors-neutral-gray-700] text-[length:--shapes-icon-size-md]" />
                                        <div className="mhg-alpha-caption absolute bottom-full left-1/2 mb-0 w-max max-w-50 translate-x-2 rounded-lg p-3 opacity-0 shadow transition-opacity duration-200 bg-white text-[--text-primary] text-sm group-hover:opacity-100 md:max-w-75 lg:left-auto lg:right-0 lg:mb-2">
                                          {
                                            processingFeeAttr.additionalInformation
                                          }
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {supportedPayments.map((supportedPayment) => {
                      const attribute = props.attributes?.find(
                        (item) => item?.attributeType === supportedPayment,
                      ) as additionalAttributeType;

                      if (!attribute) return null;

                      return (
                        <SupportedPayment
                          key={supportedPayment}
                          label={attribute?.label}
                          values={attribute?.values}
                          staticText={props.staticText}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
